import { useState, useEffect, useCallback } from "react";
import axios from "axios";

import {
  Spinner,
  Row,
  Col,
  Nav,
  Container,
  Modal,
  Button,
} from "react-bootstrap";

import { useParams, useNavigate } from "react-router-dom";

import { FaChevronDown, FaChevronUp, FaEye } from "react-icons/fa";
import { IoArrowBack } from "react-icons/io5";

import { toast } from "react-toastify";
import { TUTOR_ASSIGNMENT_API, ADMIN_COURSE_API } from "../../../api/api";

import Announcementsimg from "../../../assets/announcements-empty.png";
import Thumbnail from "../../../assets/admin-course-moduel-vides.png";
import assignThubnail from "../../../assets/student/student-assignments.png";

import VideoIcon from "../../../assets/icons/student/video.svg";

import { handleApiError } from "../../../utils/apiErrorHandler";

import "./AdminRejectedCourseManagement.css";

const AdminRejectedCourseManagement = () => {
  const navigate = useNavigate();
  const { courseId } = useParams();

  const [activeTab, setActiveTab] = useState({});
  const [rejectedCourses, setRejectedCourses] = useState([]);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  //toggle open
  const [openModuleId, setOpenModuleId] = useState(null);
  const [openAssignmentId, setOpenAssignmentId] = useState(null);

  // vide view and assignment view
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [assignmentData, setAssignmentData] = useState(null);

  //Modal
  const [isAdminReviewModalOpen, setIsAdminReviewModalOpen] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);

  //loading
  const [isLoadingCourses, setIsLoadingCourses] = useState(true);

  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  const fetchRejectedCourses = useCallback(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      setIsLoadingCourses(false);
      navigate("/admin-signin");
      return;
    }

    try {
      let response;
      if (courseId) {
        // Fetch single course
        response = await axios.post(
          ADMIN_COURSE_API.GET_ADMIN_COURSE_WITH_STATUS_ID,
          { status: "Rejected", course_id: courseId },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } else {
        // Fetch all rejected courses
        response = await axios.post(
          ADMIN_COURSE_API.GET_ADMIN_TOTAL_COURSE_WITH_STATUS,
          { status: "Rejected" },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }

      const data = response.data;

      if (data.statusCode === 200 && data.data.length > 0) {
        const normalizedCourses = data.data.map((courseData) => ({
          courseId: courseData.course_id,
          title: courseData.course_title || "N/A",
          tutor: courseData.tutor || "N/A",
          modules: courseData.total_modules || "N/A",
          duration: courseData.duration || "N/A",
          enrolled: courseData.enrolled_students || "N/A",
          category: courseData.category || "N/A",
          level: courseData.level || "N/A",
          description: courseData.description || "N/A",
          status: courseData.status || "N/A",
          modulesData: (courseData.modules || []).map((mod) => ({
            moduleId: mod.module_id,
            moduleTitle: mod.module_title || "N/A",
            moduleStatus: mod.status || "N/A",
            videos: (mod.videos || []).map((vid) => ({
              moduleVideoId: vid.module_video_id,
              videoTitle: vid.video_title || "N/A",
              videoStatus: vid.status || "N/A",
            })),
            assignments: (mod.assignments || []).map((a) => ({
              assignmentId: a.assignment_id,
              assignmentTitle: a.title,
              assignmentStatus: a.status,
            })),
          })),
        }));

        setRejectedCourses(normalizedCourses);
        // initialize tabs for all courses
        const initialTabs = {};
        normalizedCourses.forEach((c) => (initialTabs[c.courseId] = "details"));
        setActiveTab(initialTabs);
      } else {
        showErrorToast(data.message);
      }
    } catch (err) {
      console.error(err);

      // CHECK (expiry / invalid token)
      handleApiError(err, navigate, showErrorToast, "/admin-signin");
    } finally {
      setIsLoadingCourses(false);
    }
  }, [courseId, navigate]);

  useEffect(() => {
    fetchRejectedCourses();
  }, [fetchRejectedCourses]);

  const handleOpenVideo = async (moduleVideoId) => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/admin-signin");
      return;
    }

    try {
      const response = await axios.post(
        ADMIN_COURSE_API.GET_MODULE_VIDEO_BY_ID,
        { module_video_id: moduleVideoId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        setSelectedVideo(data.data);
        setIsAdminReviewModalOpen(true);
      } else {
        showErrorToast(data.message);
      }
    } catch (err) {
      console.error(err);

      // CHECK (expiry / invalid token)
      handleApiError(err, navigate, showErrorToast, "/admin-signin");
    }
  };

  const handleViewAssignment = async (assignmentId) => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/admin-signin");
      return;
    }

    try {
      const response = await axios.post(
        TUTOR_ASSIGNMENT_API.GET_ASSIGNMENT_BY_ID,
        { assignment_id: assignmentId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        setAssignmentData(data.assignment);
        setCurrentQuestionIndex(0);
        setShowAssignmentModal(true);
      } else {
        showErrorToast(data.message);
      }
    } catch (err) {
      console.error(err);
      // CHECK (expiry / invalid token)
      handleApiError(err, navigate, showErrorToast, "/admin-signin");
    }
  };

  const handleClose = () => {
    setSelectedVideo(null);
    setIsAdminReviewModalOpen(false);
  };

  const handleReconsiderVideoSubmit = async () => {
    if (!selectedVideo?.module_video_id) {
      showErrorToast("No video selected");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/admin-signin");
      return;
    }

    try {
      const response = await axios.post(
        ADMIN_COURSE_API.UPDATE_MODULE_VIDEO_STATUS,
        {
          module_video_id: selectedVideo.module_video_id,
          status: "Pending",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;
      if (data.statusCode === 200) {
        toast.success(data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
        });

        fetchRejectedCourses();
        navigate("/header/admin/course-pending");
        handleClose();
      } else {
        showErrorToast(data.message);
      }
    } catch (err) {
      console.error(err);
      // CHECK (expiry / invalid token)
      handleApiError(err, navigate, showErrorToast, "/admin-signin");
    }
  };

  const handleReconsiderAssignmentSubmit = async () => {
    if (!assignmentData?.assignment_id) {
      showErrorToast("No assignment selected");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/admin-signin");
      return;
    }

    try {
      const response = await axios.post(
        ADMIN_COURSE_API.UPDATE_ADMIN_ASSIGNMENT_STATUS,
        {
          assignment_id: assignmentData.assignment_id,
          status: "Pending",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        toast.success(data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
        });

        fetchRejectedCourses();
        setShowAssignmentModal(false);
        navigate("/header/admin/course-pending");
      } else {
        showErrorToast(data.message);
      }
    } catch (err) {
      console.error(err);

      // CHECK (expiry / invalid token)
      handleApiError(err, navigate, showErrorToast, "/admin-signin");
    }
  };

  const toggleModule = (moduleId) => {
    setOpenModuleId((prev) => (prev === moduleId ? null : moduleId));
  };

  const toggleAssignment = (assignmentId) => {
    setOpenAssignmentId((prev) =>
      prev === assignmentId ? null : assignmentId,
    );
  };

  const assignmentQuestions = assignmentData?.questions || [];
  const currentQuestion = assignmentQuestions[currentQuestionIndex];
  const isLastQuestion =
    currentQuestionIndex === assignmentQuestions.length - 1;

  return (
    <Container className="admin-reject-course-container">
      <div className="admin-pending-backmoving-button-wrapper">
        <button
          className="admin-pending-backmoving-button-main"
          onClick={() => navigate("/header/admin/all-courses")}
        >
          <IoArrowBack className="admin-pending-backmoving-button-icon" />
          <span className="admin-pending-backmoving-button-text">Back</span>
        </button>
      </div>
      <h4 className="admin-dashboard-title">Rejected Courses</h4>
      <p className="admin-dashboard-subtitle">
        Review rejected courses, review feedback, and reconsider course videos
        for approval.
      </p>

      {isLoadingCourses ? (
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : rejectedCourses.length === 0 ? (
        <div className="tutor-assignment-empty-box">
          <div className="tutor-assignment-empty-content text-center mt-5">
            <img
              src={Announcementsimg}
              alt="empty"
              className="tutor-announcements-section-empty-img mb-3"
            />
            <h5>No rejected courses found.</h5>
          </div>
        </div>
      ) : (
        rejectedCourses.map((course) => (
          <div
            key={course.courseId}
            className="admin-reject-course-header-container mb-4"
          >
            <div className="admin-reject-course-header">
              <h5 className="admin-reject-course-title">
                {course.title}
                <p
                  className={`admin-reject-course-badge admin-reject-course-status-${course.status?.toLowerCase()}`}
                >
                  {course.status}
                </p>
              </h5>
              <p className="admin-reject-course-subtitle">
                Review rejected course modules and videos, verify their content
                and details, and reconsider each item for approval.
              </p>
            </div>

            {/* Tabs */}
            <Row className="justify-content-start">
              <Col lg={4} md={8} sm={12}>
                <Nav variant="tabs" className="admin-reject-course-tabs">
                  <Nav.Item>
                    <Nav.Link
                      active={activeTab[course.courseId] === "details"}
                      onClick={() =>
                        setActiveTab((prev) => ({
                          ...prev,
                          [course.courseId]: "details",
                        }))
                      }
                    >
                      Course Details
                    </Nav.Link>
                  </Nav.Item>

                  <Nav.Item>
                    <Nav.Link
                      active={activeTab[course.courseId] === "modules"}
                      onClick={() =>
                        setActiveTab((prev) => ({
                          ...prev,
                          [course.courseId]: "modules",
                        }))
                      }
                    >
                      Modules
                    </Nav.Link>
                  </Nav.Item>

                  <Nav.Item>
                    <Nav.Link
                      active={activeTab[course.courseId] === "assignments"}
                      onClick={() =>
                        setActiveTab((prev) => ({
                          ...prev,
                          [course.courseId]: "assignments",
                        }))
                      }
                    >
                      Assignments
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
            </Row>

            {/* Details Tab */}
            {activeTab[course.courseId] === "details" && (
              <div className="admin-reject-course-content">
                <div className="admin-reject-course-info-grid">
                  <div className="admin-reject-course-info-box">
                    <p>Tutor</p>
                    <h6>{course.tutor}</h6>
                  </div>
                  <div className="admin-reject-course-info-box">
                    <p>Modules</p>
                    <h6>{course.modules}</h6>
                  </div>
                  <div className="admin-reject-course-info-box">
                    <p>Duration</p>
                    <h6>{course.duration}</h6>
                  </div>
                  <div className="admin-reject-course-info-box">
                    <p>Enrolled Students</p>
                    <h6>{course.enrolled}</h6>
                  </div>
                </div>

                <div className="admin-reject-course-bottom">
                  <Col md={6}>
                    <p>Category</p>
                    <h6>{course.category}</h6>
                  </Col>
                  <Col md={6}>
                    <p>Level</p>
                    <h6>{course.level}</h6>
                  </Col>
                </div>

                <div className="admin-reject-course-description">
                  <p>Description</p>
                  <span>{course.description}</span>
                </div>
              </div>
            )}

            {/* Modules Tab */}
            {activeTab[course.courseId] === "modules" && (
              <div className="admin-reject-course-modules">
                {course.modulesData.length === 0 ? (
                  <div className="tutor-mycourse-module-emptypart-wrapper">
                    <div className="tutor-mycourse-module-emptypart-container">
                      <img
                        src={Announcementsimg}
                        alt="empty"
                        className="tutor-announcements-section-empty-img mb-3"
                      />
                      <p className="tutor-mycourse-module-emptypart-title">
                        No rejected course Modules found.
                      </p>
                    </div>
                  </div>
                ) : (
                  course.modulesData.map((module, index) => {
                    const isOpen = openModuleId === module.moduleId;

                    return (
                      <div
                        key={module.moduleId}
                        className="admin-reject-course-module-card"
                      >
                        {/* Module Header */}
                        <div
                          className="admin-reject-course-module-header"
                          onClick={() => toggleModule(module.moduleId)}
                        >
                          <h6 className="admin-reject-course-module-title">
                            <span className="admin-reject-course-module-id">
                              {index + 1}
                            </span>
                            <span className="admin-reject-course-module-name">
                              {module.moduleTitle}
                            </span>
                          </h6>

                          <div className="admin-reject-course-module-statu-both">
                            <span
                              className={`admin-reject-course-module-status admin-reject-course-status-${module.moduleStatus?.toLowerCase()}`}
                            >
                              {module.moduleStatus}
                            </span>
                            <span className="admin-reject-course-module-arrow">
                              {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                            </span>
                          </div>
                        </div>

                        {/* Videos */}
                        {isOpen && (
                          <div className="admin-reject-course-module-videos">
                            {module.videos.map((video) => (
                              <div
                                key={video.moduleVideoId}
                                className="admin-reject-course-video-row"
                              >
                                <div className="admin-reject-course-video-both-set">
                                  <img
                                    src={Thumbnail}
                                    alt={video.videoTitle}
                                    className="admin-reject-course-video-rowimageee"
                                  />
                                  <span className="admin-reject-course-video-title">
                                    {video.videoTitle}
                                  </span>
                                </div>

                                <div className="admin-reject-course-video-actions">
                                  <span
                                    className={`admin-reject-course-module-status admin-reject-course-status-${video.videoStatus?.toLowerCase()}`}
                                  >
                                    {video.videoStatus}
                                  </span>

                                  <button
                                    className="admin-reject-course-play-btn"
                                    onClick={() =>
                                      handleOpenVideo(video.moduleVideoId)
                                    }
                                  >
                                    <img
                                      src={VideoIcon}
                                      alt="video"
                                      className="admin-published-course-lesson-icon"
                                    />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* ASSIGNMENTS TAB */}
            {activeTab[course.courseId] === "assignments" && (
              <div className="admin-reject-course-modules">
                {course.modulesData.every(
                  (module) =>
                    !module.assignments || module.assignments.length === 0,
                ) ? (
                  <div className="tutor-mycourse-module-emptypart-wrapper">
                    <div className="tutor-mycourse-module-emptypart-container">
                      <img
                        src={Announcementsimg}
                        alt="empty"
                        className="tutor-announcements-section-empty-img mb-3"
                      />
                      <p className="tutor-mycourse-module-emptypart-title">
                        No rejected assignments found.
                      </p>
                    </div>
                  </div>
                ) : (
                  course.modulesData.map((module, index) =>
                    module.assignments?.map((assignment) => {
                      const isOpen =
                        openAssignmentId === assignment.assignmentId;

                      return (
                        <div
                          key={assignment.assignmentId}
                          className="admin-reject-course-module-card"
                        >
                          {/* ASSIGNMENT HEADER (like module header) */}
                          <div
                            className="admin-reject-course-module-header"
                            onClick={() =>
                              toggleAssignment(assignment.assignmentId)
                            }
                          >
                            <h6 className="admin-reject-course-module-title">
                              <span className="admin-reject-course-module-id">
                                {index + 1}
                              </span>
                              <span className="admin-reject-course-module-name">
                                {module.moduleTitle}
                              </span>
                            </h6>

                            <div className="admin-reject-course-module-statu-both">
                              <span
                                className={`admin-reject-course-module-status admin-reject-course-status-${assignment.assignmentStatus?.toLowerCase()}`}
                              >
                                {assignment.assignmentStatus}
                              </span>
                              <span className="admin-reject-course-module-arrow">
                                {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                              </span>
                            </div>
                          </div>

                          {/* ASSIGNMENT BODY */}
                          {isOpen && (
                            <div className="admin-reject-course-module-videos">
                              <div className="admin-reject-course-video-row">
                                <div className="admin-reject-course-video-both-set">
                                  <img
                                    src={assignThubnail}
                                    alt={assignment.assignmentTitle}
                                    className="admin-reject-course-video-rowimageee"
                                  />

                                  <span className="admin-reject-course-video-title">
                                    {assignment.assignmentTitle}
                                  </span>
                                </div>
                                <div className="admin-reject-course-video-actions">
                                  <span
                                    className={`admin-reject-course-module-status admin-reject-course-status-${assignment.assignmentStatus?.toLowerCase()}`}
                                  >
                                    {assignment.assignmentStatus}
                                  </span>

                                  <FaEye
                                    className="admin-pending-course-view-icon"
                                    title="View Assignment"
                                    onClick={() =>
                                      handleViewAssignment(
                                        assignment.assignmentId,
                                      )
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    }),
                  )
                )}
              </div>
            )}
          </div>
        ))
      )}

      {/* Video Modal */}
      <Modal
        show={isAdminReviewModalOpen}
        onHide={handleClose}
        size="lg"
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="admin-reject-course-modal"
      >
        <div className="admin-reject-course-modal-content">
          <Modal.Header
            closeButton
            className="admin-reject-course-modal-header"
          />

          <Modal.Body className="admin-reject-course-modal-body">
            {selectedVideo && (
              <>
                <div className="admin-reject-course-modal-video-section">
                  <video
                    className="admin-reject-course-modal-video"
                    controls
                    controlsList="nodownload"
                    // noplaybackrate
                    disablePictureInPicture
                    onContextMenu={(e) => e.preventDefault()}
                  >
                    <source src={selectedVideo.video_url} type="video/mp4" />
                  </video>
                </div>

                <div className="admin-reject-course-modal-feedback">
                  <label className="admin-reject-course-modal-feedback-labelll">
                    Reason for Rejection
                  </label>
                  <div className="admin-reject-course-modal-feedback-content">
                    {selectedVideo.reason || "No reject reason provided"}
                  </div>
                </div>
              </>
            )}
          </Modal.Body>

          <Modal.Footer className="admin-reject-course-modal-footer">
            <Button
              className="admin-reject-course-reconsider-btn"
              onClick={handleReconsiderVideoSubmit}
            >
              Reconsider Video
            </Button>
          </Modal.Footer>
        </div>
      </Modal>

      {/* ASSIGNMENT REVIEW MODAL */}
      <Modal
        show={showAssignmentModal}
        onHide={() => setShowAssignmentModal(false)}
        size="lg"
        centered
        backdrop="static"
        keyboard={false}
        className="admin-pending-course-view-assignment-modal p-0"
      >
        <Modal.Header
          closeButton
          className="admin-pending-course-view-assignment-modal-header"
        >
          <div className="admin-pending-course-view-assignment-header-left">
            <h5 className="admin-pending-course-view-assignment-title">
              {assignmentData?.assignment_title}
            </h5>
            <p className="admin-pending-course-view-assignment-subtitle">
              Assignment •{" "}
              <span className="admin-pending-course-view-assignment-span">
                {assignmentQuestions.length} Questions
              </span>
            </p>
          </div>

          <div className="admin-pending-course-view-assignment-header-right">
            {String(currentQuestionIndex + 1).padStart(2, "0")} /{" "}
            {assignmentQuestions.length} Questions
          </div>
        </Modal.Header>

        <Modal.Body className="admin-pending-course-view-assignment-modal-body">
          {currentQuestion && (
            <div className="admin-pending-course-view-assignment-question-card">
              <div className="admin-pending-course-view-assignment-question-header">
                <span className="admin-pending-course-view-assignment-question-number">
                  {currentQuestionIndex + 1}.
                </span>
                <span className="admin-pending-course-view-assignment-question-text">
                  {currentQuestion.question}
                </span>

                <span className="admin-pending-course-view-assignment-question-mark">
                  1 Mark
                </span>
              </div>

              {/* OPTIONS */}
              <div className="admin-pending-course-view-assignment-options">
                {["a", "b", "c", "d"].map((key) => {
                  const isCorrect = currentQuestion.answer === key;

                  return (
                    <label
                      key={key}
                      className={`admin-pending-course-view-assignment-option ${
                        isCorrect ? "correct" : ""
                      }`}
                    >
                      <input type="radio" checked={isCorrect} readOnly />
                      <span>{currentQuestion[key]}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* FEEDBACK */}
          <div className="admin-pending-course-view-assignment-feedback">
            <label className="admin-pending-course-view-assignment-feedback-label">
              Reason for Rejection
            </label>
            <textarea
              className="admin-pending-course-view-assignment-feedback-textarea form-control"
              rows="5"
              value={assignmentData?.reason || "No reject reason provided"}
              readOnly
            />
          </div>
        </Modal.Body>

        <Modal.Footer className="admin-pending-course-view-assignment-modal-footer">
          {currentQuestionIndex > 0 ? (
            <Button
              className="admin-pending-course-view-assignment-modal-back"
              onClick={() => setCurrentQuestionIndex((prev) => prev - 1)}
            >
              Back
            </Button>
          ) : (
            <div />
          )}

          {!isLastQuestion ? (
            <Button
              className="admin-pending-course-view-assignment-modal-next"
              onClick={() => setCurrentQuestionIndex((p) => p + 1)}
            >
              Next
            </Button>
          ) : (
            <div className="admin-pending-course-view-assignment-action-buttons">
              <Button
                className="admin-reject-course-reconsider-btn"
                onClick={handleReconsiderAssignmentSubmit}
              >
                Reconsider Assignment
              </Button>
            </div>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminRejectedCourseManagement;
